import { useState } from 'react';
import { Database, Tag, Pencil, Check, Folder } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import type { ProposedSchema } from '@/types/plan';

interface DataSchemaPreviewCardProps {
  schema: ProposedSchema;
  onUpdate: (schema: ProposedSchema) => void;
  onCreateDraft: () => void;
  isCreating?: boolean;
}

export function DataSchemaPreviewCard({
  schema,
  onUpdate,
  onCreateDraft,
  isCreating,
}: DataSchemaPreviewCardProps) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (field: string, value: string) => {
    setEditingField(field);
    setEditValue(value);
  };

  const commitEdit = (field: keyof ProposedSchema) => {
    onUpdate({ ...schema, [field]: editValue });
    setEditingField(null);
  };

  const fields = schema.fields || [];

  const typeColors: Record<string, string> = {
    string: '#3B82F6',
    number: '#10B981',
    boolean: '#F59E0B',
    date: '#8B5CF6',
    array: '#EF4444',
    object: '#6366F1',
  };

  return (
    <div
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.sm,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          background: `${FOREST_GREEN.DEFAULT}08`,
          borderBottom: `1px solid ${BORDER.DEFAULT}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Database style={{ width: 16, height: 16, color: FOREST_GREEN.DEFAULT }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: FOREST_GREEN.DEFAULT }}>
          Data Schema Preview
        </span>
        {fields.length > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: MID_GRAY.DEFAULT }}>
            {fields.length} field{fields.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 300, overflowY: 'auto' }}>
        {/* Name */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
            Name
          </label>
          {editingField === 'name' ? (
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `1px solid ${BORDER.DEFAULT}`, fontSize: 14 }}
                onKeyDown={(e) => e.key === 'Enter' && commitEdit('name')}
              />
              <button onClick={() => commitEdit('name')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <Check style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{schema.name}</span>
              <button
                onClick={() => startEdit('name', schema.name)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.5 }}
              >
                <Pencil style={{ width: 12, height: 12 }} />
              </button>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
            Description
          </label>
          {editingField === 'description' ? (
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `1px solid ${BORDER.DEFAULT}`, fontSize: 13 }}
                onKeyDown={(e) => e.key === 'Enter' && commitEdit('description')}
              />
              <button onClick={() => commitEdit('description')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <Check style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: MID_GRAY.dark }}>{schema.description}</span>
              <button
                onClick={() => startEdit('description', schema.description)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.5 }}
              >
                <Pencil style={{ width: 12, height: 12 }} />
              </button>
            </div>
          )}
        </div>

        {/* Group + Tags row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {schema.groupName && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 10,
                background: `${FOREST_GREEN.DEFAULT}15`,
                color: FOREST_GREEN.DEFAULT,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Folder style={{ width: 10, height: 10 }} />
              {schema.groupName}
            </span>
          )}
          {schema.tags?.map((tag) => (
            <span
              key={tag}
              style={{
                fontSize: 11,
                padding: '2px 6px',
                borderRadius: 10,
                background: `${MID_GRAY.DEFAULT}10`,
                color: MID_GRAY.DEFAULT,
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <Tag style={{ width: 10, height: 10 }} />
              {tag}
            </span>
          ))}
        </div>

        {/* Fields table */}
        {fields.length > 0 && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>
              Fields
            </label>
            <div
              style={{
                border: `1px solid ${BORDER.DEFAULT}`,
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              {/* Table header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px 50px',
                  gap: 0,
                  padding: '6px 10px',
                  background: '#F9FAFB',
                  borderBottom: `1px solid ${BORDER.DEFAULT}`,
                  fontSize: 10,
                  fontWeight: 600,
                  color: MID_GRAY.DEFAULT,
                  textTransform: 'uppercase',
                }}
              >
                <span>Name</span>
                <span>Type</span>
                <span>Req</span>
              </div>
              {/* Table rows */}
              {fields.map((field, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 80px 50px',
                    gap: 0,
                    padding: '5px 10px',
                    borderBottom: i < fields.length - 1 ? `1px solid ${BORDER.DEFAULT}` : 'none',
                    fontSize: 12,
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 500, color: '#1F2937' }}>{field.name}</span>
                    {field.description && (
                      <div style={{ fontSize: 10, color: MID_GRAY.DEFAULT, marginTop: 1 }}>{field.description}</div>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: 8,
                      background: `${typeColors[field.type] || MID_GRAY.DEFAULT}12`,
                      color: typeColors[field.type] || MID_GRAY.DEFAULT,
                      width: 'fit-content',
                    }}
                  >
                    {field.type}
                  </span>
                  <span style={{ fontSize: 11, color: field.required ? FOREST_GREEN.DEFAULT : MID_GRAY.DEFAULT }}>
                    {field.required ? 'Yes' : 'No'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: `1px solid ${BORDER.DEFAULT}`,
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
        }}
      >
        <button
          onClick={onCreateDraft}
          disabled={isCreating}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            background: FOREST_GREEN.DEFAULT,
            color: WHITE,
            fontSize: 13,
            fontWeight: 500,
            cursor: isCreating ? 'not-allowed' : 'pointer',
            opacity: isCreating ? 0.6 : 1,
          }}
        >
          {isCreating ? 'Creating...' : 'Create Schema'}
        </button>
      </div>
    </div>
  );
}
